/**
 * useCommunity — community-wall feature orchestration hook.
 *
 * Owns the message list, pagination cursor, live-listener lifecycle, optimistic
 * text + image send, reply state, unread/scrolled-away tracking, tab-visibility
 * pause, deep-link rehydration of the creator `User`, and the access/blocked
 * derivations the page renders. Presentation-layer only.
 *
 * Modeled on `useChat` (`@/presentation/feature/chat/hooks/useChat`) — same
 * ownership split (`presentation.md` § Page + feature-hook split): the hook owns
 * state, business logic, lifecycle side-effects, and deep-link rehydration; it
 * exposes memoized `onPageInit` / `onPageVisible` / `onPageHidden` /
 * `onPageDispose` the page wires into its own `useBasePage` (the hook NEVER calls
 * `useBasePage`). The community message build lives in the Task-6 use cases — it
 * is NOT re-implemented here.
 *
 * Differences from the chat port (community has no equivalent surface):
 *  - NO presence dispatch — community has no `chattingWith` equivalent. We
 *    consume `useTabVisibility` directly (rather than `useChatPresence`) to keep
 *    `isTabVisibleRef` fresh and re-raise the unread pill on tab-resume.
 *  - NO E2EE — community messages are not encrypted.
 *  - NO read receipts / mark-seen — a community wall has none.
 *  - NO send-error banner / `success`-flip / row dimming on a failed send:
 *    community-Flutter (`community_screen.dart:459-550`, `addMessage`) never
 *    touches `success` — it stays `true` regardless of the write result and the
 *    message is never dimmed (unlike the chat screen, which flips + dims). The
 *    optimistic entry's `success` is never mutated.
 *  - DELIBERATE DIVERGENCE FROM FLUTTER on a failed send: community-Flutter
 *    leaves a failed message in the list; the web `sendText` REMOVES a failed
 *    message from the list by `id`, but ONLY when it is of type text
 *    (image/video failures are left in place — a retry mechanism is coming in a
 *    later story). `sendImage` therefore leaves its failed tile untouched (the
 *    "Processing" row persists; the sheet's failure toast is the user signal).
 *    See `sendText` / `sendImage` below.
 *
 * Flutter source of truth:
 *  - `community_screen.dart:129-197` (`initState` / `onBuildCompleted`) — on
 *    access, `getMessages()` then `subscribeToLiveMessages()` (live broadcasting
 *    excluded — out of scope).
 *  - `community_screen.dart:133-143` — `_isCurrentUser` / `_hasAccess`.
 *  - `community_screen.dart:250-278` (`getMessages`) — paginate; `_isFirstLoad`
 *    flips false after the first page; `_mostRecentMessage` from page[0] on first
 *    load (drives the live cursor).
 *  - `community_screen.dart:199-248` (`visibleListItemsListener`) — bottom-index
 *    tracking, unread flush at bottom (`:205-211`), `_isScrolledUp` flip
 *    (`:235-239`), pagination trigger (`:241-246`). The web list (Task 11) owns
 *    the scroll math + the `communityMessagesLoadMoreOffset` distance; this hook
 *    exposes `onScrolledToBottom` / `onScrolledAway` / `onLoadMore` the list
 *    drives.
 *  - `community_screen.dart:459-550` (`addMessage`, text branch) — optimistic
 *    insert at index 0, clear reply, scroll to top, then persist.
 *  - `community_screen.dart:398-457` (`_addMediaMessage`, media branch) — captures
 *    the reply target, composer text, and the tap-time timestamp into the built
 *    message, then hands it to the add-content navigation. The web `sendImage`
 *    builds the same optimistic entity and inserts it IN-PAGE (WEB DEVIATION —
 *    the web sheet is in-page, not a route push; the live listener's id-dedupe
 *    reconciles the echo). No trim/empty guard on the media path (`:398-457`; the
 *    empty guard is text-path-only, `:472-474`).
 *  - `community_screen.dart:106-127` (`didChangeAppLifecycleState`) — tab
 *    background/resume handling of the unread pill.
 *  - `community_screen.dart:280-284` — live cursor = `_mostRecentMessage
 *    .createdTimestamp` or `now` when empty.
 *  - `community_screen.dart:729-745` (`_onReplyTap`) — toggle reply target.
 *
 * No unit test is mandated for a feature hook (the convention only requires
 * tests for use cases).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';
import { useSelector } from 'react-redux';

import type { RootState } from '@/presentation/store';
import {
  selectExclusiveContentLoaded,
  selectHasActiveSubscription,
} from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';

import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import { CommunityChatMessageRemapper } from '@/domain/remappers/community/communityChatMessageRemapper';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';

import { FetchCommunityMessagesUseCase } from '@/domain/usecases/community/fetchCommunityMessagesUseCase';
import { SubscribeToCommunityMessagesUseCase } from '@/domain/usecases/community/subscribeToCommunityMessagesUseCase';
import { AddCommunityMessageUseCase } from '@/domain/usecases/community/addCommunityMessageUseCase';
import { SendCommunityImageMessageUseCase } from '@/domain/usecases/community/sendCommunityImageMessageUseCase';
import { SendCommunityVideoMessageUseCase } from '@/domain/usecases/community/sendCommunityVideoMessageUseCase';
import { LikeCommunityMessageUseCase } from '@/domain/usecases/community/likeCommunityMessageUseCase';
import { getUserUseCase } from '@/domain/usecases/user/getUserUseCase';

import { appLogger } from '@/domain/services/analytics/appLogger';
import { sortCommunityMessagesByCreatedDesc } from '@/domain/utils/communityUtils';

import { useTabVisibility } from '@/presentation/utils/tabVisibility';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

import { useCommunityLiveListener } from './useCommunityLiveListener';

export interface UseCommunityArgs {
  /** URL `:userId` parameter from the route. Stable for the life of the page. */
  ownerIdParam: string;
  /**
   * Pre-resolved creator `UserEntity` from `location.state` (in-app navigation
   * path — `Navigator.navigateToCommunity` forwards the full struct; Task 13).
   * When present, the hook seeds `creatorUser` with this value and skips the
   * deep-link rehydration round-trip. When `null` (deep-link / refresh path
   * where `location.state` is absent), the hook resolves the creator via
   * `getUserUseCase` during `onPageInit`.
   */
  initialCreatorUser: UserEntity | null;
  /**
   * Invoked when a like toggle fails (`!success` or a thrown error) so the
   * PAGE can surface the failure UI (toast) — the hook stays free of UI
   * invocations (`presentation.md` § Page + feature-hook split). Fired BEFORE
   * the hook reverts the optimistic count delta, matching Flutter's
   * toast-then-revert order (`communit_message_widget.dart:163-168`). Must be
   * referentially stable (memoized by the caller).
   */
  onLikeMessageError: () => void;
}

/**
 * Arguments for `sendImage` — a single community-wall IMAGE send.
 *
 * Unlike `sendText` (which reads its reply target from live hook state at send
 * time), the composer captures `text`, `capturedAtMillis`, AND `replyToMessage`
 * at the moment the add-media button is TAPPED (Task 8), then hands all three
 * here after the add-content sheet confirms. This mirrors Flutter, which
 * snapshots the composer text, the tap-time timestamp, and the reply target into
 * the built message BEFORE navigating (`_addMediaMessage`,
 * `community_screen.dart:399`/`:443`/`:400-432`) and clears the live reply target
 * the instant the add-content route pushes (`didPushNext`, `:176-183`). Reading
 * them from hook state at confirm time would therefore diverge from Flutter.
 */
export interface SendCommunityImageArgs {
  /** The selected image file to upload (raw, un-transcoded). */
  file: File;
  /** Client-computed aspect ratio (width / height) of the selected image. */
  aspectRatio: number;
  /**
   * The composer draft captured at the add-media tap (`community_screen.dart:443`).
   * May be `''` — an image message with empty text is valid; there is NO
   * trim/empty guard on the media path (unlike the text path, `:472-474`).
   */
  text: string;
  /**
   * The `Date.now()` snapshot taken when the add-media button was tapped. Flutter
   * stamps `createdTimestamp` at tap time (`:399` → assigned `:446`), NOT at
   * confirm time, so the persisted wall sort position matches Flutter regardless
   * of how long the user dwells in the add-content sheet.
   */
  capturedAtMillis: number;
  /**
   * The reply target captured at the add-media tap (`community_screen.dart:400-432`),
   * or omitted when the composer was not in reply mode.
   */
  replyToMessage?: CommunityChatMessageEntity;
}

/**
 * Arguments for `sendVideo` — a single community-wall VIDEO send. Mirrors
 * `SendCommunityImageArgs` exactly (`_addMediaMessage` is ONE media path for both
 * image and video — `community_screen.dart:398-461`) plus `durationSeconds`, the
 * video-only metadatum the upload's `buildUploadFileInfo` needs.
 *
 * Like the image args, the composer captures `text`, `capturedAtMillis`, AND
 * `replyToMessage` at the moment the add-media button is TAPPED (Task 8) — video
 * reuses the SAME tap-time snapshot the image flow captures
 * (`CommunityComposer.tsx` `handleAddMedia`), so reading them from live hook
 * state at confirm time would diverge from Flutter (`_addMediaMessage`,
 * `community_screen.dart:399`/`:443`/`:400-432`).
 */
export interface SendCommunityVideoArgs {
  /** The selected video file to upload (raw, un-transcoded). */
  file: File;
  /** Client-computed aspect ratio (width / height) of the selected video. */
  aspectRatio: number;
  /**
   * The video duration in seconds (`videoMeta.duration`, captured by the sheet).
   * Fed to `SendCommunityVideoMessageUseCase` → the upload's `buildUploadFileInfo`
   * (no image equivalent — images have no duration).
   */
  durationSeconds: number;
  /**
   * The composer draft captured at the add-media tap (`community_screen.dart:443`).
   * May be `''` — a video message with empty text is valid; there is NO
   * trim/empty guard on the media path (the guard is text-path-only, `:472-474`).
   */
  text: string;
  /**
   * The `Date.now()` snapshot taken when the add-media button was tapped. Flutter
   * stamps `createdTimestamp` at tap time (`:399` → assigned `:446`), NOT at
   * confirm time, so the persisted wall sort position matches Flutter regardless
   * of how long the user dwells in the add-content sheet.
   */
  capturedAtMillis: number;
  /**
   * The reply target captured at the add-media tap (`community_screen.dart:400-432`),
   * or omitted when the composer was not in reply mode.
   */
  replyToMessage?: CommunityChatMessageEntity;
}

export interface UseCommunityResult {
  /**
   * The resolved creator user (the wall owner). `null` while deep-link
   * rehydration is in flight (the page renders a loading indicator gated on
   * `isFirstLoad || creatorUser === null`). Populated synchronously when the
   * caller supplied `initialCreatorUser`.
   */
  creatorUser: UserEntity | null;
  /** Newest-first message list (lower index = newer). Sorted desc by createdTimestamp. */
  messages: CommunityChatMessageEntity[];
  /**
   * True until the first page has been fetched (mirrors Flutter `_isFirstLoad`).
   * On the deep-link path this also covers the rehydration round-trip — stays
   * `true` from mount until both the creator is resolved AND the first message
   * page lands (or access is denied / the wall is empty).
   */
  isFirstLoad: boolean;
  /** True while a pagination page is in-flight (mirrors Flutter `_isFetching`). */
  isFetchingMore: boolean;
  /** True iff a further page exists (mirrors Flutter `_hasMore`). */
  hasMore: boolean;
  /**
   * True iff the current user can read/post on this wall: the wall owner always
   * has access, otherwise an active exclusive-content subscription is required.
   * Mirrors `community_screen.dart:138-143`
   * (`_isCurrentUser || exclusiveContentSubscriptionsUsersIds.contains(...)`).
   */
  hasAccess: boolean;
  /** True iff the current user is the wall owner (`community_screen.dart:137`). */
  isCurrentUser: boolean;
  /**
   * True iff the creator is blocked or suspended
   * (`creatorUser.blocked || creatorUser.suspended`) — drives the page's blocked
   * placeholder (`community_screen.dart:104` `_userBlockedMessage`).
   */
  isCreatorBlocked: boolean;
  /**
   * The list of unread messages currently bucketed (sorted the same way the
   * main `messages` list is). Exposed so the page can format the most-recent
   * unread item into the unread-pill preview. Flushed to `messages` on
   * `onScrolledToBottom`.
   */
  unreadItems: CommunityChatMessageEntity[];
  /**
   * True for ~3 s after every message-while-scrolled-away arrival (mirrors
   * Flutter `_unreadMessageReceived`, `community_screen.dart:323-334`). The page
   * gates the unread pill on `unreadBannerVisible && unreadItems.length > 0`.
   */
  unreadBannerVisible: boolean;
  /**
   * True iff the user is currently scrolled away from the bottom of the
   * (reversed) list. Mirrors Flutter's `_isScrolledUp`
   * (`community_screen.dart:235-239`) — exposed so the page can render the
   * scroll-down button.
   */
  isScrolledUp: boolean;
  /**
   * The message currently being replied to, or `null`. Mirrors Flutter's
   * `_messageToReplyNotifier` (`community_screen.dart:72`). Drives the composer
   * reply-preview chip.
   */
  messageToReply: CommunityChatMessageEntity | null;
  /**
   * Monotonically-increasing counter bumped every time `sendText` inserts an
   * optimistic message. The list owner watches this token and snaps the viewport
   * to the top so the just-sent message lands visible — mirrors Flutter
   * `_itemScrollController.scrollTo(index: 0, …)` (`community_screen.dart:539-543`).
   */
  scrollToTopToken: number;
  /**
   * Send a text message. Trims + non-empty guards (`community_screen.dart:472-474`),
   * builds the entity SYNCHRONOUSLY via `CommunityChatMessageRemapper.fromInput`,
   * then optimistically inserts it at index 0 + sort, clears the reply target,
   * and bumps `scrollToTopToken` — all BEFORE awaiting the write via
   * `AddCommunityMessageUseCase.execute(message)`, mirroring Flutter's "add
   * message in list before saving to DB" ordering (`community_screen.dart:533-548`).
   * The UI updates instantly; the write completes afterward.
   *
   * DELIBERATE DIVERGENCE FROM FLUTTER on a failed write: community-Flutter
   * (`community_screen.dart:459-550`) never touches `success` and leaves the
   * failed message rendered normally. The web instead REMOVES the failed message
   * from the list by `id` — but ONLY when it is of type text. Removal is by `id`
   * (NOT index) because other messages may arrive while this write is in flight.
   * Image/video failures are intentionally left in place (a retry mechanism is
   * coming in a later story). The message's `success` is still never flipped and
   * the row is never dimmed (unlike chat).
   */
  sendText: (text: string) => Promise<void>;
  /**
   * Send an IMAGE message (`_addMediaMessage`, `community_screen.dart:398-457`).
   * Builds the optimistic entity SYNCHRONOUSLY via
   * `CommunityChatMessageRemapper.fromInput` (image branch) from the composer's
   * tap-time snapshot (`args` — see `SendCommunityImageArgs`), with NO trim/empty
   * guard (an image with empty text is valid — media-path parity, the empty guard
   * is text-path-only, `:472-474`). Then optimistically inserts it at index 0 +
   * sort and bumps `scrollToTopToken`, BEFORE awaiting the upload — mirroring
   * `sendText`'s "insert before persist" ordering and the web chat-media flow
   * (`useChat.sendMedia`).
   *
   * WEB DEVIATION (documented): Flutter's media message reaches its list via the
   * live listener's `added` branch after the doc write (the user is away on the
   * add-content screens, `community_screen.dart:286-336`); the web sheet is
   * in-page, so the hook inserts the optimistic entity immediately and the live
   * listener's id-dedupe reconciles the echo (`useCommunityLiveListener.ts:138-147`),
   * while its `modified` handler delivers the CF's processing→done flip. Does NOT
   * touch `messageToReply` — the composer already cleared it at sheet-open
   * (Task 8; Flutter `didPushNext` parity, `:176-183`).
   *
   * Awaits `SendCommunityImageMessageUseCase.execute` and RETURNS its upload
   * boolean so the sheet (Task 7) can toast on failure. DELIBERATE image-failure
   * divergence: on `false` the failed tile STAYS in the list (the "Processing"
   * row persists; the sheet's `onError` toast is the user-facing signal) —
   * `success` is never flipped and the message is never removed by `id` (unlike
   * `sendText`, which removes failed TEXT messages only; a retry mechanism is
   * coming in a later story — see the file header).
   */
  sendImage: (args: SendCommunityImageArgs) => Promise<boolean>;
  /**
   * Send a VIDEO message (`_addMediaMessage`, `community_screen.dart:398-461` —
   * the same media path image uses). The video sibling of `sendImage`: builds the
   * optimistic entity SYNCHRONOUSLY via `CommunityChatMessageRemapper.fromInput`
   * (media branch, `type = CommunityChatMessageType.video`) from the composer's
   * tap-time snapshot (`args` — see `SendCommunityVideoArgs`), with NO trim/empty
   * guard (a video with empty text is valid — media-path parity, the guard is
   * text-path-only, `:472-474`). Then optimistically inserts it at index 0 + sort
   * and bumps `scrollToTopToken`, BEFORE awaiting the upload — identical ordering
   * to `sendImage` (`community_screen.dart:533-548`; web chat-media flow
   * `useChat.sendMedia`).
   *
   * WEB DEVIATION (documented, same as `sendImage`): Flutter's media message
   * reaches its list via the live listener's `added` branch after the doc write;
   * the web sheet is in-page, so the hook inserts the optimistic entity
   * immediately and the live listener's id-dedupe reconciles the echo
   * (`useCommunityLiveListener.ts:138-147`), while its `modified` handler delivers
   * the CF's processing→done flip. Does NOT touch `messageToReply` — the composer
   * already cleared it at sheet-open (Task 8; Flutter `didPushNext` parity,
   * `:176-183`).
   *
   * Awaits `SendCommunityVideoMessageUseCase.execute` and RETURNS its upload
   * boolean so the sheet (Task 17) can toast on failure. DELIBERATE video-failure
   * divergence (same as the image flow, file-header `:28-34`): on `false` the
   * failed tile STAYS in the list (the "Processing" row persists; the sheet's
   * `onError` toast is the user-facing signal) — `success` is never flipped and
   * the message is never removed by `id`. A retry mechanism is coming in a later
   * story.
   */
  sendVideo: (args: SendCommunityVideoArgs) => Promise<boolean>;
  /**
   * Toggle the reply target (`_onReplyTap`, `community_screen.dart:729-745`):
   * when a target is already set, passing any message clears it; otherwise the
   * passed message becomes the target. Passing `null` always clears.
   */
  setReplyTarget: (message: CommunityChatMessageEntity | null) => void;
  /**
   * Toggle the like on a community message — the presentation half of Flutter's
   * `_onLikePress` (`communit_message_widget.dart:120-171`) plus the
   * `CommunityMessageLikesCountNotifier` role (the notifier only exists because
   * Flutter's per-widget state can't reach the shared list; on the web the list
   * state lives here in `setMessages`, so no separate notifier is needed).
   *
   * `liked` is the NEW desired state supplied by the button (`onToggle(!isLiked)`
   * — the web form of Flutter's `!_messageLike!.liked`). No-op for the user's own
   * message (`:121-125`) and while a like for the same message is already
   * in-flight (`_isLikingMessage`, `:121-127`). Applies an optimistic in-list
   * count delta BEFORE the write (`:129-144, 154-155`); on `!success` invokes the
   * page-supplied `onLikeMessageError` callback (which shows the toast) and then
   * reverts the delta (`:163-168`). Deliberately does NOT clamp the count
   * to 0 (Flutter parity — differs from the `useContentComments.likeComment`
   * sibling, `useContentComments.ts:373`).
   */
  likeMessage: (message: CommunityChatMessageEntity, liked: boolean) => Promise<void>;
  /**
   * Optimistically flip a live message's own wall tile to `isLive: false` — the
   * web analog of Flutter's own-list flip inside `endLiveMessage`
   * (`community_screen.dart:653-659`). The page calls this when the BROADCASTER
   * returns from a live session THEY initiated (the `onPopNext` path, gated on a
   * pending-broadcast ref), so the just-ended tile reflects the end the instant
   * they return — independent of the Firestore listener echo, which does not
   * re-render the broadcaster's own tile on the broadcaster's own end write
   * (the community page is keep-alive; see finding 1).
   *
   * Local-only + idempotent: it does NOT double-write Firestore (the
   * `isLive: false` merge already happened in `useLiveStreaming.onPageDispose` via
   * `EndCommunityLiveStreamUseCase`). If the listener echo won the race, the
   * `isLive === false` no-op guard prevents a redundant re-render — the end state
   * is identical either way. No-op when no message with `messageId` is present.
   */
  markLiveMessageEnded: (messageId: string) => void;
  /**
   * The list owner calls this when the user reaches the bottom of the (reversed)
   * list. Flushes `unreadItems` into `messages` and clears the pill
   * (`community_screen.dart:205-211`).
   */
  onScrolledToBottom: () => void;
  /**
   * The list owner calls this on the inverse transition (scrolled away from
   * bottom) so the live-listener `added` branch routes subsequent arrivals into
   * the unread bucket from the very first one.
   */
  onScrolledAway: () => void;
  /** The list owner calls this when the top sentinel intersects ("load older"). */
  onLoadMore: () => void;
  // --- Page lifecycle handlers (wired into the page's `useBasePage`) ---
  /**
   * `useBasePage.onInit` target. Idempotent — guarded by an internal ref.
   * Deep-link rehydrates the creator (when needed) then, if `hasAccess`, fetches
   * page 1 and starts the live listener with the correct cursor.
   */
  onPageInit: () => void;
  /**
   * `useBasePage.onPopNext` target. Re-raises the unread pill when the page
   * becomes visible again and unread items are bucketed
   * (`community_screen.dart:114-125`).
   */
  onPageVisible: () => void;
  /**
   * `useBasePage.onPushNext` / `onPop` target. Clears the reply target on
   * push-next (`community_screen.dart:176-183`). No presence dispatch (community
   * has none).
   */
  onPageHidden: () => void;
  /**
   * `useBasePage.onDispose` target. Tears down the live listener and clears the
   * unread-pill timer.
   */
  onPageDispose: () => void;
}

export const useCommunity = ({
  ownerIdParam,
  initialCreatorUser,
  onLikeMessageError,
}: UseCommunityArgs): UseCommunityResult => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Selector: current user has an active subscription to the creator's
  // exclusive content (web mirror of
  // `exclusiveContentSubscriptionsUsersIds.contains(user.firebaseId)`,
  // `community_screen.dart:142`). Use `ownerIdParam` (always present, stable)
  // so the selector binds on first render.
  const currentUserSubscribedToCreator = useSelector((state: RootState) =>
    selectHasActiveSubscription(state, ownerIdParam),
  );

  // True once the global `exclusiveContent` slice has hydrated from
  // SecureStorage (`ExclusiveContentListener`'s `GetExclusiveContentSubscriptions
  // UseCase` reload completes and flips `isLoaded`). Until then the slice holds
  // its empty initial state, so `currentUserSubscribedToCreator` reads a FALSE
  // negative for a genuine subscriber. The access gate below must NOT run for a
  // non-owner until this is `true`, otherwise the one-shot initial load skips
  // the fetch + live subscription for a subscriber whose subscription simply
  // hasn't been mirrored into Redux yet (the Test 1 / Test 8 race). Flutter does
  // not hit this because its `ExclusiveContentNotifier` is fully hydrated
  // synchronously at app boot before any screen mounts; the web mirror hydrates
  // asynchronously, so we wait for it here.
  const exclusiveContentLoaded = useSelector(selectExclusiveContentLoaded);

  // --- Use-case instances (one per hook lifetime) ---
  const fetchCommunityMessagesUseCase = useMemo(
    () => new FetchCommunityMessagesUseCase(),
    [],
  );
  const subscribeToCommunityMessagesUseCase = useMemo(
    () => new SubscribeToCommunityMessagesUseCase(),
    [],
  );
  const addCommunityMessageUseCase = useMemo(
    () => new AddCommunityMessageUseCase(),
    [],
  );
  const sendCommunityImageMessageUseCase = useMemo(
    () => new SendCommunityImageMessageUseCase(),
    [],
  );
  const sendCommunityVideoMessageUseCase = useMemo(
    () => new SendCommunityVideoMessageUseCase(),
    [],
  );
  const likeCommunityMessageUseCase = useMemo(
    () => new LikeCommunityMessageUseCase(),
    [],
  );

  // --- Local state ---
  // `creatorUser` is held as state so deep-link rehydration can fill it in
  // during `onPageInit`. Seeded synchronously from `initialCreatorUser` when
  // the in-app nav path supplied it (no rehydration round-trip needed).
  const [creatorUser, setCreatorUser] = useState<UserEntity | null>(
    () => initialCreatorUser,
  );
  const [messages, setMessages] = useState<CommunityChatMessageEntity[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  // `_unreadListItems` in Flutter — messages arrived while the user is scrolled
  // away from the bottom (or the tab is hidden). Held as state so the pill
  // re-renders; flushed into `messages` on `onScrolledToBottom`.
  const [unreadItems, setUnreadItems] = useState<CommunityChatMessageEntity[]>([]);
  // Transient flag mirroring Flutter's `_unreadMessageReceived`. Flipped to
  // `true` on every message-while-scrolled-away arrival (tab visible); the
  // listener's 3 s timer flips it back. Reset on scroll-back-to-bottom.
  const [unreadBannerVisible, setUnreadBannerVisible] = useState<boolean>(false);
  // React-state mirror of `isAtBottomRef` (inverted). Drives the scroll-down
  // button render. Mirrors Flutter's `_isScrolledUp`
  // (`community_screen.dart:235-239`).
  const [isScrolledUp, setIsScrolledUp] = useState<boolean>(false);
  // `_messageToReplyNotifier.value` — the message being replied to, or null.
  const [messageToReply, setMessageToReply] =
    useState<CommunityChatMessageEntity | null>(null);
  // Monotonic counter — bumped on every optimistic insert in `sendText`. The
  // list owner watches this to snap the viewport to the top (Flutter
  // `_itemScrollController.scrollTo(index: 0, …)`, `community_screen.dart:539-543`).
  const [scrollToTopToken, setScrollToTopToken] = useState<number>(0);

  // --- Refs used inside async callbacks / the once-bound live listener ---
  // (mirror `useChat`: handlers registered ONCE cannot read render-closure
  // state — they read refs.)
  const messagesRef = useRef<CommunityChatMessageEntity[]>([]);
  messagesRef.current = messages;

  const unreadItemsRef = useRef<CommunityChatMessageEntity[]>([]);
  unreadItemsRef.current = unreadItems;

  // Always-current snapshot of `creatorUser`. State can be `null` on the
  // deep-link path until rehydration completes; lifecycle callbacks and the
  // initial-load flow read through this ref to act on the post-rehydrate value
  // without waiting for a re-render closure.
  const creatorUserRef = useRef<UserEntity | null>(creatorUser);
  creatorUserRef.current = creatorUser;

  // Pagination cursor — the `createdTimestamp` of the oldest message in the
  // most recent page (Firestore `startAfter` cursor). Mirrors Flutter
  // `_lastFetchedDocument` semantically.
  const lastTimestampRef = useRef<number | null>(null);

  // Live-listener cursor — the most-recent message's `createdTimestamp` at the
  // moment the listener starts, or `now` when the wall is empty. Mirrors
  // Flutter `_mostRecentMessage.createdTimestamp` (`community_screen.dart:281-284`).
  const listenerCursorRef = useRef<number | null>(null);

  // Live-listener cleanup handle.
  const liveUnsubscribeRef = useRef<Unsubscribe | null>(null);

  // True while a pagination request is in-flight. Held as a ref (NOT only
  // state) so `onLoadMore` short-circuits synchronously on rapid re-fires.
  const isFetchingRef = useRef<boolean>(false);

  // Per-message in-flight guard for likes — the web mirror of Flutter's
  // `_isLikingMessage` (`communit_message_widget.dart:121-127, 170`), keyed by
  // message id so concurrent likes on different messages don't block each other.
  // A ref (not state) so `likeMessage` short-circuits synchronously on a
  // double-tap before the optimistic re-render lands.
  const likingMessageIdsRef = useRef<Set<string>>(new Set());

  // True iff the user is currently pinned near the bottom of the list. Read
  // synchronously by the live-listener `added` branch to route arrivals;
  // driven explicitly by `onScrolledToBottom` / `onScrolledAway`.
  const isAtBottomRef = useRef<boolean>(true);

  // Web mirror of Flutter `_appLifecycleState` (`community_screen.dart:106-127`):
  // `true` when the tab is visible, `false` when hidden. The live-listener
  // `added` branch reads this synchronously to route hidden-tab arrivals to the
  // unread bucket. Defaults `true` under SSR. Kept fresh by `useTabVisibility`.
  const isTabVisibleRef = useRef<boolean>(
    typeof document === 'undefined'
      ? true
      : document.visibilityState === 'visible',
  );

  // Pending 3 s auto-hide timer for `unreadBannerVisible`. Held in a ref so the
  // listener (outside React's render closure) can clear/reschedule it across
  // consecutive arrivals, and the dispose path can cancel it.
  const unreadBannerTimerRef = useRef<number | null>(null);

  // Guards to ensure single-fire side-effects.
  const hasInitializedRef = useRef<boolean>(false);
  const hasStartedListenerRef = useRef<boolean>(false);
  // True once the deferred initial-load effect has dispatched `runInitialLoad`.
  // The effect re-runs whenever `accessResolvable` flips; this ref makes sure
  // the load fires exactly once across those re-runs.
  const hasStartedInitialLoadRef = useRef<boolean>(false);
  // True once the access-gained re-load effect has dispatched `runInitialLoad`
  // after `hasAccess` flipped to `true` post-subscribe. Set synchronously
  // BEFORE the (async) dispatch so a spurious re-render during the in-flight
  // fetch (before `hasStartedListenerRef` is set at Step 4) cannot double-fire.
  const hasStartedAccessGainedLoadRef = useRef<boolean>(false);
  // `useBasePage.onInit` requested the initial load. Held as state (not only a
  // ref) so the deferred-load gating effect re-evaluates when the request lands
  // and again when the access gate becomes resolvable. We record the intent
  // rather than running immediately, because for a non-owner the access gate
  // inside `runInitialLoad` is only reliable once the exclusive-content slice
  // has hydrated (`accessResolvable`).
  const [wantsInit, setWantsInit] = useState<boolean>(false);

  // --- Derivations (Flutter `community_screen.dart:137-143`) ---
  // `_isCurrentUser = currentUser.firebaseId == user.firebaseId`. Use
  // `ownerIdParam` (URL-stable) so this is correct on first render and after
  // rehydration alike.
  const isCurrentUser: boolean =
    currentUser?.firebaseId === ownerIdParam;
  // `_hasAccess = _isCurrentUser || subscriptions.contains(owner)` — the owner
  // always has access even without a subscription
  // (`community_screen.dart:138-143`).
  const hasAccess: boolean = isCurrentUser || currentUserSubscribedToCreator;
  // `creatorUser.blocked || creatorUser.suspended` — drives the blocked
  // placeholder (`community_screen.dart:104`).
  const isCreatorBlocked: boolean = creatorUser
    ? creatorUser.blocked || creatorUser.suspended
    : false;

  // True iff `hasAccess` can be evaluated reliably right now. The owner's
  // access is independent of the exclusive-content slice (it derives from
  // `currentUser` + `ownerIdParam`, both present on first render), so the gate
  // is always resolvable for the owner. For a non-owner the gate depends on
  // `selectHasActiveSubscription`, which is a FALSE negative until the slice
  // hydrates — so we only consider it resolvable once `exclusiveContentLoaded`
  // is `true`. The initial load is deferred (below) until this is `true`,
  // closing the Test 1 / Test 8 race where a subscriber's read + live
  // subscription were skipped because the gate ran before hydration.
  const accessResolvable: boolean = isCurrentUser || exclusiveContentLoaded;

  // --- Live-listener handlers (Task 9, bound ONCE) ---
  const { handleAdded, handleModified, handleRemoved } = useCommunityLiveListener({
    isAtBottomRef,
    isTabVisibleRef,
    setMessages,
    setUnreadItems,
    setUnreadBannerVisible,
    unreadBannerTimerRef,
  });

  // Re-arm the transient unread pill for 3 s, mirroring the listener's timer so
  // there is exactly ONE auto-hide timer (`unreadBannerTimerRef`). Used by the
  // tab-resume branch to replicate the resume half of Flutter's lifecycle
  // handler (`community_screen.dart:114-125`): when the tab returns and a
  // message arrived while hidden, re-show the pill for 3 s (the hidden-tab timer
  // scheduled at arrival has usually already expired unseen).
  const raiseUnreadPill = useCallback(() => {
    setUnreadBannerVisible(true);
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
    }
    unreadBannerTimerRef.current = window.setTimeout(() => {
      setUnreadBannerVisible(false);
      unreadBannerTimerRef.current = null;
    }, ANIMATION_CONSTANTS.duration.autoHideDelay);
  }, []);

  // --- Tab-visibility (keep `isTabVisibleRef` fresh + resume the pill) ---
  // Community has NO presence dispatch (no `chattingWith` equivalent), so we
  // consume `useTabVisibility` directly rather than through `useChatPresence`.
  useTabVisibility((isVisible) => {
    isTabVisibleRef.current = isVisible;
    // Flutter resume branch (`community_screen.dart:114-125`): on return to the
    // foreground, if a message arrived while hidden, re-show the unread pill for
    // 3 s.
    if (isVisible && unreadItemsRef.current.length > 0) {
      raiseUnreadPill();
    }
  });

  // --- Initial load + listener bootstrap ---
  //
  // Mirrors `community_screen.dart:191-197` (`onBuildCompleted`) plus deep-link
  // rehydration (the URL-only entry path the page can't fully describe):
  //  0. (Deep-link only) Resolve the creator via `getUserUseCase` when
  //     `initialCreatorUser` was absent.
  //  1. If `!hasAccess`, stop — the page shows the subscribe/blocked
  //     placeholder. No fetch, no listener.
  //  2. Fetch page 1; seed `messages` + `lastTimestampRef` + `hasMore`.
  //  3. Capture the live cursor = page[0].createdTimestamp ?? now
  //     (`community_screen.dart:269-271, 281-284`).
  //  4. Start the live listener (`community_screen.dart:194`).
  const runInitialLoad = useCallback(async () => {
    if (!currentUser) return;

    try {
      // --- Step 0: deep-link rehydration (if needed) ---
      let resolvedCreator: UserEntity | null = creatorUserRef.current;
      if (resolvedCreator === null) {
        const fetched = await getUserUseCase.execute(ownerIdParam);
        if (fetched === null) {
          appLogger.error(
            'useCommunity: creator user not found on deep-link rehydrate',
            'useCommunity.runInitialLoad',
            { error: new Error(`ownerId=${ownerIdParam} not found`) },
          );
          return;
        }
        resolvedCreator = fetched;
        setCreatorUser(resolvedCreator);
      }

      // --- Step 1: access gate (`community_screen.dart:192`) ---
      // Re-derive access from the resolved creator: the owner always has access
      // (`community_screen.dart:138-143`). `currentUserSubscribedToCreator` is
      // captured from the render closure (stable for this page's life).
      const ownerHasAccess =
        currentUser.firebaseId === resolvedCreator.firebaseId ||
        currentUserSubscribedToCreator;
      if (!ownerHasAccess) {
        // No fetch / no listener — the page renders the subscribe placeholder.
        return;
      }

      // --- Step 2: fetch page 1 ---
      const limit = SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION;
      const page = await fetchCommunityMessagesUseCase.execute(
        resolvedCreator.firebaseId,
        null,
        limit,
      );

      setMessages(page.messages);
      lastTimestampRef.current = page.lastTimestamp;
      setHasMore(page.hasMore);

      // --- Step 3: live cursor (`community_screen.dart:269-271, 281-284`) ---
      const mostRecent = page.messages[0];
      listenerCursorRef.current = mostRecent?.createdTimestamp ?? Date.now();

      // --- Step 4: start the live listener (`community_screen.dart:194`) ---
      if (!hasStartedListenerRef.current) {
        hasStartedListenerRef.current = true;
        liveUnsubscribeRef.current = subscribeToCommunityMessagesUseCase.execute(
          resolvedCreator.firebaseId,
          listenerCursorRef.current,
          {
            onAdded: handleAdded,
            onModified: handleModified,
            onRemoved: handleRemoved,
          },
        );
      }
    } catch (error) {
      appLogger.error('Initial community load failed', 'useCommunity.runInitialLoad', {
        error: error as Error,
      });
    } finally {
      // `_isFirstLoad = false` (`community_screen.dart:272`) — flips after the
      // first page regardless of outcome so the page can drop its first-load
      // indicator (and render the placeholder when access is denied).
      setIsFirstLoad(false);
    }
  }, [
    currentUser,
    ownerIdParam,
    currentUserSubscribedToCreator,
    fetchCommunityMessagesUseCase,
    subscribeToCommunityMessagesUseCase,
    handleAdded,
    handleModified,
    handleRemoved,
  ]);

  // --- onLoadMore (pagination, `community_screen.dart:250-278`) ---
  const onLoadMore = useCallback((): void => {
    if (isFetchingRef.current || !hasMore) {
      return;
    }
    const creator = creatorUserRef.current;
    if (creator === null) return;
    if (lastTimestampRef.current === null) return; // first-load not complete yet
    isFetchingRef.current = true;
    setIsFetchingMore(true);

    void (async () => {
      try {
        const limit = SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION;
        const page = await fetchCommunityMessagesUseCase.execute(
          creator.firebaseId,
          lastTimestampRef.current,
          limit,
        );

        if (page.messages.length === 0) {
          setHasMore(page.hasMore);
          return;
        }

        // Merge: existing messages are newer (lower index), the new page is
        // older (higher index). Concat + sort desc handles any out-of-order
        // arrivals defensively (mirrors `useChat.onLoadMore`).
        setMessages((prev) => {
          const next = prev.concat(page.messages);
          next.sort(sortCommunityMessagesByCreatedDesc);
          return next;
        });

        lastTimestampRef.current = page.lastTimestamp;
        setHasMore(page.hasMore);
      } catch (error) {
        appLogger.error('Paginate older community messages failed', 'useCommunity.onLoadMore', {
          error: error as Error,
        });
      } finally {
        isFetchingRef.current = false;
        setIsFetchingMore(false);
      }
    })();
  }, [hasMore, fetchCommunityMessagesUseCase]);

  // --- onScrolledToBottom (`community_screen.dart:205-211`) ---
  // Flush unread items into the visible list (Flutter inserts `_unreadListItems
  // .reversed` at index 0 then clears the bucket) and clear the pill.
  const onScrolledToBottom = useCallback((): void => {
    isAtBottomRef.current = true;
    setIsScrolledUp(false);

    if (unreadItemsRef.current.length === 0) {
      return;
    }

    const pending = unreadItemsRef.current;
    setMessages((prev) => {
      const next = prev.concat(pending);
      next.sort(sortCommunityMessagesByCreatedDesc);
      return next;
    });
    setUnreadItems([]);
    setUnreadBannerVisible(false);
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
      unreadBannerTimerRef.current = null;
    }
  }, []);

  // --- onScrolledAway (inverse transition) ---
  const onScrolledAway = useCallback((): void => {
    isAtBottomRef.current = false;
    setIsScrolledUp(true);
  }, []);

  // --- sendText (`community_screen.dart:459-550`, text branch) ---
  const sendText = useCallback(
    async (text: string): Promise<void> => {
      // Trim + non-empty guard (`community_screen.dart:472-474`).
      if (text.trim() === '') return;
      if (!currentUser) return;
      const creator = creatorUserRef.current;
      if (creator === null) return;

      // Build the optimistic entity SYNCHRONOUSLY via the canonical remapper
      // (`CommunityChatMessageRemapper.fromInput` — the build logic lives in the
      // domain layer, NOT re-implemented here). `messageToReply` becomes the
      // reply target.
      const message = CommunityChatMessageRemapper.fromInput({
        owner: creator.firebaseId,
        // Flutter sends the raw controller text (`community_screen.dart:523`) —
        // the trim above is only the empty-guard, not applied to the payload.
        text,
        currentUser,
        replyToMessage: messageToReply ?? undefined,
      });

      // Optimistic insert BEFORE the write — Flutter inserts at index 0, clears
      // the reply, and scrolls to the top, THEN awaits the persist (the "add
      // message in list before saving to DB" ordering,
      // `community_screen.dart:533-548`). The list updates instantly; the
      // network round-trip happens afterward.
      setMessages((prev) => {
        const next = [message, ...prev];
        next.sort(sortCommunityMessagesByCreatedDesc);
        return next;
      });
      // Clear the reply target + unfocus is the page's concern; clear the
      // target here (`community_screen.dart:538`).
      setMessageToReply(null);
      // Snap the viewport to the top (own send) — Flutter
      // `_itemScrollController.scrollTo(index: 0, …)` (`community_screen.dart:539-543`).
      setScrollToTopToken((prev) => prev + 1);

      // ONLY NOW await the Firestore write — the use case persists the
      // already-built entity (`community_screen.dart:548`) and reports the
      // outcome.
      const { success } = await addCommunityMessageUseCase.execute(message);

      // Failed write: DELIBERATE DIVERGENCE FROM FLUTTER. Community-Flutter
      // (`community_screen.dart:459-550`) leaves the optimistically-inserted
      // message in the list regardless of the write result (it never touches
      // `success`). On the web we remove a failed message from the list, but
      // ONLY when it is of type text — remove it by `id` (NOT index) because
      // other messages may have arrived while this write was in flight, so an
      // index-based removal is unsafe. Image/video failures are intentionally
      // left in place (a retry mechanism is coming in a later story).
      if (!success && message.type === CommunityChatMessageType.text) {
        setMessages((prev) => prev.filter((m) => m.id !== message.id));
      }
    },
    [currentUser, addCommunityMessageUseCase, messageToReply],
  );

  // --- sendMedia (shared optimistic-send body for image + video) ---
  // Flutter's `_addMediaMessage` (`community_screen.dart:398-461`) is ONE media
  // path for BOTH image and video. This helper is the single source of truth for
  // the optimistic-insert-before-upload body that `sendImage` and `sendVideo`
  // otherwise duplicated verbatim — they differ ONLY in the media `type` stamped
  // on the built entity and the concrete upload use case invoked (video also
  // forwards `durationSeconds`). Keeping the body here means the two send paths
  // evolve in lockstep and the media-path parity notes below apply to BOTH.
  //
  // Builds the optimistic entity SYNCHRONOUSLY via the canonical remapper (media
  // branch, terminal `type` — the build logic lives in the domain layer, NOT
  // re-implemented here; the remapper's media branch yields
  // `mediaInfo: { isUploading: true, aspectRatio }`,
  // `communityChatMessageRemapper.ts:298-311/313-325`) from the composer's
  // tap-time snapshot (`text` / `capturedAtMillis` / `replyToMessage` were all
  // captured at the add-media tap, NOT read from live hook state; see
  // `SendCommunityImageArgs` / `SendCommunityVideoArgs`). `capturedAtMillis`
  // becomes the message's `createdTimestamp` (tap-time parity,
  // `community_screen.dart:399`/`:446`); `replyToMessage` is the tap-time reply
  // target the composer captured (Task 8); the CF fills `path`/`thumbnail`. NO
  // trim/empty guard — a media message with empty text is valid (media-path
  // parity, `:398-461`; the empty guard is text-path-only, `:472-474`).
  //
  // Optimistic insert at index 0 + sort and a `scrollToTopToken` bump BEFORE the
  // upload await — same ordering as `sendText` (`community_screen.dart:533-548`)
  // and the web chat-media flow (`useChat.sendMedia`). WEB DEVIATION: Flutter's
  // media message reaches its list via the live listener's `added` branch after
  // the doc write (the user is away on the add-content screens, `:286-336`); the
  // web sheet is in-page, so we insert immediately and the listener's id-dedupe
  // reconciles the echo (`useCommunityLiveListener.ts:138-147`), while its
  // `modified` handler delivers the CF's processing→done flip. Does NOT touch
  // `messageToReply` — the composer already cleared it at sheet-open (Task 8;
  // Flutter `didPushNext` parity, `:176-183`).
  //
  // The `upload` callback runs the type-specific use case and returns the REAL
  // upload boolean so the sheet can toast on failure. On `false`: DELIBERATE
  // media-failure divergence (see file header) — leave the failed tile in place
  // (the "Processing" row persists; the sheet's `onError` toast is the user-facing
  // signal). Never flip `success`, never remove by `id`. A retry mechanism is
  // coming in a later story.
  const sendMedia = useCallback(
    async (
      type: CommunityChatMessageType,
      args: {
        text: string;
        aspectRatio: number;
        capturedAtMillis: number;
        replyToMessage?: CommunityChatMessageEntity;
      },
      upload: (message: CommunityChatMessageEntity) => Promise<boolean>,
    ): Promise<boolean> => {
      if (!currentUser) return false;
      const creator = creatorUserRef.current;
      if (creator === null) return false;

      const message = CommunityChatMessageRemapper.fromInput({
        owner: creator.firebaseId,
        text: args.text,
        currentUser,
        replyToMessage: args.replyToMessage,
        media: {
          type,
          aspectRatio: args.aspectRatio,
          capturedAtMillis: args.capturedAtMillis,
        },
      });

      setMessages((prev) => {
        const next = [message, ...prev];
        next.sort(sortCommunityMessagesByCreatedDesc);
        return next;
      });
      setScrollToTopToken((prev) => prev + 1);

      // ONLY NOW await the upload→doc-write→transcode chain.
      const uploaded = await upload(message);
      return uploaded;
    },
    [currentUser],
  );

  // --- sendImage (`_addMediaMessage`, `community_screen.dart:398-457`) ---
  // The community IMAGE send — a thin wrapper over the shared `sendMedia` body
  // (image `type` + `SendCommunityImageMessageUseCase`), returning its upload
  // boolean so the sheet (Task 7) can toast on failure.
  const sendImage = useCallback(
    (args: SendCommunityImageArgs): Promise<boolean> =>
      sendMedia(CommunityChatMessageType.image, args, (message) =>
        sendCommunityImageMessageUseCase.execute({
          file: args.file,
          message,
        }),
      ),
    [sendMedia, sendCommunityImageMessageUseCase],
  );

  // --- sendVideo (`_addMediaMessage`, `community_screen.dart:398-461`) ---
  // The community VIDEO send — the video sibling of `sendImage`: a thin wrapper
  // over the shared `sendMedia` body (video `type` +
  // `SendCommunityVideoMessageUseCase`, additionally forwarding `durationSeconds`),
  // returning its upload boolean so the sheet (Task 17) can toast on failure.
  const sendVideo = useCallback(
    (args: SendCommunityVideoArgs): Promise<boolean> =>
      sendMedia(CommunityChatMessageType.video, args, (message) =>
        sendCommunityVideoMessageUseCase.execute({
          file: args.file,
          durationSeconds: args.durationSeconds,
          message,
        }),
      ),
    [sendMedia, sendCommunityVideoMessageUseCase],
  );

  // --- setReplyTarget (`_onReplyTap`, `community_screen.dart:729-745`) ---
  const setReplyTarget = useCallback(
    (message: CommunityChatMessageEntity | null): void => {
      // Passing `null` always clears. Otherwise toggle: when a target is already
      // set, tapping any message clears it (`community_screen.dart:730-734`);
      // when none is set, the tapped message becomes the target (`:743`).
      setMessageToReply((prev) => {
        if (message === null) return null;
        if (prev !== null) return null;
        return message;
      });
    },
    [],
  );

  // --- likeMessage (`_onLikePress`, `communit_message_widget.dart:120-171`) ---
  // The presentation half of the like toggle: own-message + in-flight guards,
  // the optimistic in-list count delta (the web replacement for Flutter's
  // `CommunityMessageLikesCountNotifier`, whose only job is to reach the shared
  // list state that lives here on the web), the use-case call, and the count
  // revert on failure (the failure toast is a UI invocation the page owns and
  // supplies via `onLikeMessageError`). The local-storage write / re-tint is the
  // use case's concern (Task 3); this handler owns only the count + the revert.
  const likeMessage = useCallback(
    async (message: CommunityChatMessageEntity, liked: boolean): Promise<void> => {
      if (!currentUser) return;
      // Own-message guard (`communit_message_widget.dart:121-125`): the author
      // cannot like their own message — the button still renders, the tap no-ops.
      if (currentUser.firebaseId === message.messageUsersInfo.userId) return;
      // Per-message in-flight guard (`_isLikingMessage`, `:121-127`).
      if (likingMessageIdsRef.current.has(message.id)) return;
      likingMessageIdsRef.current.add(message.id); // `:127`

      // Optimistic count delta (`communit_message_widget.dart:129-144`). Applied
      // to the shared list BEFORE the await (`:154-155`), same mapping as
      // `useContentComments.likeComment`. DELIBERATELY no `Math.max(0, …)` clamp:
      // Flutter does not clamp (`currentLikes = liked ? likes + 1 : likes - 1`,
      // `:129-144`) — this intentionally differs from the sibling
      // `useContentComments.likeComment` (`useContentComments.ts:373`), Flutter
      // parity wins.
      const delta = liked ? 1 : -1;
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, likes: m.likes + delta } : m)),
      );

      // Shared failure UX (`communit_message_widget.dart:164-168`): notify the
      // page first (it shows the toast — UI invocations are page-owned per
      // `presentation.md`), then revert the applied delta (`message.likes` is by
      // then the optimistic value, so subtracting the delta restores the pre-tap
      // count) — matching Flutter's failure-branch side-effect order. Used by both
      // the `!success` branch and the thrown-error `catch`.
      const notifyErrorAndRevert = (): void => {
        onLikeMessageError();
        setMessages((prev) =>
          prev.map((m) => (m.id === message.id ? { ...m, likes: m.likes - delta } : m)),
        );
      };

      try {
        // Task-3 use case (`ContentReactionsRepository.likeCommunityMessage`,
        // `communit_message_widget.dart:161-162`).
        const success = await likeCommunityMessageUseCase.execute({
          messageId: message.id,
          messageParentId: message.parentId,
          currentUserId: currentUser.firebaseId,
          liked,
        });
        if (!success) notifyErrorAndRevert();
      } catch (error) {
        // A thrown storage/use-case error is a failed like: log it (sibling
        // precedent `useContentComments.likeComment`, `useContentComments.ts:422-424`)
        // and run the same failure branch so the optimistic delta cannot stick.
        appLogger.error('Error liking community message', 'useCommunity.likeMessage', {
          error: error as Error,
        });
        notifyErrorAndRevert();
      } finally {
        likingMessageIdsRef.current.delete(message.id); // `:170`
      }
    },
    [currentUser, likeCommunityMessageUseCase, onLikeMessageError],
  );

  // --- Page lifecycle callbacks ---
  // `useBasePage` is page-only per `presentation.md`. The host page wires its
  // own `useBasePage` and forwards each phase into the callbacks below.

  const onPageInit = useCallback((): void => {
    // Idempotent guard — `useBasePage.onInit` fires once, but the guard makes
    // the intent explicit and protects against a StrictMode double-mount.
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    // Record the intent; the deferred effect below runs the actual load once
    // the access gate is resolvable (immediately for the owner, after the
    // exclusive-content slice hydrates for a non-owner).
    setWantsInit(true);
  }, []);

  // Deferred initial-load runner. Fires `runInitialLoad` exactly once, as soon
  // as `useBasePage.onInit` requested it (`wantsInit`) AND the access gate is
  // resolvable (`accessResolvable` — true immediately for the owner, true for a
  // non-owner only once the exclusive-content slice has hydrated). This closes
  // the Test 1 / Test 8 race: a subscriber whose subscription had not yet been
  // mirrored into Redux when `onInit` fired now waits for hydration, so the
  // first-page read + the collection-group live subscription fire reliably once
  // access becomes available — instead of being silently skipped (and the page
  // reaching `ready` empty). The owner path is unchanged (resolvable on render
  // 1), and a genuine non-subscriber still reaches the gate with `hasAccess`
  // false → no fetch, no listener, subscribe-CTA shown (Flutter parity).
  useEffect(() => {
    if (!wantsInit || !accessResolvable) return;
    if (hasStartedInitialLoadRef.current) return;
    hasStartedInitialLoadRef.current = true;
    void runInitialLoad();
  }, [wantsInit, accessResolvable, runInitialLoad]);

  // --- Access-gained re-load (subscribe-from-within-community) ---
  //
  // The initial load above is one-shot. When a non-owner opens a creator's
  // community WITHOUT an active subscription, that one-shot pass runs the access
  // gate inside `runInitialLoad`, hits the `!ownerHasAccess` early return, and
  // never fetches messages or starts the live listener (`hasStartedListenerRef`
  // stays `false`). The subscribe CTA is shown.
  //
  // On the web the subscribe flow is an in-page overlay (`SubscribeUserSheet`),
  // NOT a route push that would rebuild the page — so when the subscription
  // completes and `currentUserSubscribedToCreator` (→ `hasAccess`) flips to
  // `true`, the page swaps the CTA for the message list, but nothing ever
  // re-runs the fetch. The list renders empty until a hard refresh (which
  // remounts the hook with `hasAccess` already `true`). Flutter does not hit
  // this: there the subscribe sheet is a pushed route and the community screen
  // captures a fresh `_hasAccess` when it becomes visible again.
  //
  // Fix (per the finding): when `hasAccess` flips to `true` AFTER the initial
  // load has already dispatched (`hasStartedInitialLoadRef`) but the live
  // listener was never started (`!hasStartedListenerRef` — the tell-tale of a
  // prior no-access pass), fetch page 1 and start the listener exactly once.
  // `runInitialLoad` is idempotent for this: it re-derives `ownerHasAccess`
  // from the now-active subscription (via its `currentUserSubscribedToCreator`
  // closure dep, so the callback identity changes when it flips), fetches, and
  // guards the listener start with `hasStartedListenerRef`. The owner never
  // reaches here (its initial pass always starts the listener), and a genuine
  // non-subscriber never flips `hasAccess`.
  useEffect(() => {
    if (!hasStartedInitialLoadRef.current) return; // initial load hasn't run yet
    if (hasStartedListenerRef.current) return; // messages already loaded + listening
    if (hasStartedAccessGainedLoadRef.current) return; // re-load already dispatched
    if (!hasAccess) return; // still gated
    hasStartedAccessGainedLoadRef.current = true;
    void runInitialLoad();
  }, [hasAccess, runInitialLoad]);

  // Optimistic own-wall live-end flip — the web analog of Flutter's own-list
  // flip inside `endLiveMessage` (`community_screen.dart:653-659`,
  // `message.broadcastingInfo!.isLive = false`). Called by the page when the
  // BROADCASTER returns from a live session they initiated, so the just-ended
  // tile flips immediately, independent of the Firestore listener echo (the
  // community page is keep-alive, so the broadcaster's own end write never
  // re-renders their own tile on its own — see finding 1). In-place replaces the
  // matching message with a copy whose typed `broadcastingInfo.isLive` is
  // `false`; the tile reads that entity field directly. No re-sort needed —
  // order is unchanged.
  const markLiveMessageEnded = useCallback((messageId: string): void => {
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === messageId);
      if (idx === -1) return prev;
      const target = prev[idx];
      // No-op if already ended (avoids a needless re-render if the listener echo
      // won the race — the end state is identical either way).
      const info = target.broadcastingInfo;
      if (info && info.isLive === false) return prev;
      const next = prev.slice();
      next[idx] = {
        ...target,
        // Spread the typed struct (all-required scalars) and flip `isLive`; a
        // `type = live` tile always carries `broadcastingInfo`, but stay defensive
        // if it is somehow absent (nothing to flip → leave undefined).
        broadcastingInfo: info ? { ...info, isLive: false } : undefined,
      };
      return next;
    });
  }, []);

  const onPageVisible = useCallback((): void => {
    // `useBasePage.onPopNext` — returning to this route. Flutter's resume branch
    // (`community_screen.dart:114-125`) re-raises the unread pill when a message
    // arrived while away.
    if (unreadItemsRef.current.length > 0) {
      raiseUnreadPill();
    }
  }, [raiseUnreadPill]);

  const onPageHidden = useCallback((): void => {
    // `useBasePage.onPushNext` / `onPop` — Flutter `didPushNext`
    // (`community_screen.dart:176-183`) clears the reply target (input unfocus
    // is the composer's concern). No presence dispatch (community has none).
    setMessageToReply(null);
  }, []);

  const onPageDispose = useCallback((): void => {
    // Final teardown — tear down the live listener + clear the pill timer
    // (`community_screen.dart:157-171`).
    if (liveUnsubscribeRef.current) {
      liveUnsubscribeRef.current();
      liveUnsubscribeRef.current = null;
    }
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
      unreadBannerTimerRef.current = null;
    }
  }, []);

  return {
    creatorUser,
    messages,
    isFirstLoad,
    isFetchingMore,
    hasMore,
    hasAccess,
    isCurrentUser,
    isCreatorBlocked,
    unreadItems,
    unreadBannerVisible,
    isScrolledUp,
    messageToReply,
    scrollToTopToken,
    sendText,
    sendImage,
    sendVideo,
    setReplyTarget,
    likeMessage,
    markLiveMessageEnded,
    onScrolledToBottom,
    onScrolledAway,
    onLoadMore,
    onPageInit,
    onPageVisible,
    onPageHidden,
    onPageDispose,
  };
};
